// src/utils/permissions.ts
import { UserRole } from '@/types/models';

// Tipos para los permisos
export type ResourceAction = 'list' | 'create' | 'edit' | 'delete' | 'show';
export type ResourceName = 
  | 'lockers' 
  | 'locker-controllers'
  | 'locker-assignments'
  | 'users'
  | 'hotels'
  | 'groups'
  | 'zones'
  | 'locker-types'
  | 'tags'
  | 'tag-types'
  | 'alerts'
  | 'system'
  | 'dashboard';

// Definición de permisos por rol
export interface RolePermissions {
  [resource: string]: {
    [action in ResourceAction]?: boolean;
  };
}

// Mapa de permisos por rol
const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  SuperAdmin: {
    // SuperAdmin tiene acceso total a todo
    '*': {
      list: true,
      create: true,
      edit: true,
      delete: true,
      show: true,
    },
  },
  GroupAdmin: {
    // GroupAdmin puede gestionar hoteles y usuarios de su grupo
    hotels: {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    users: {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    lockers: {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    'locker-controllers': {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },
    'locker-assignments': {
      list: true,
      create: true,
      edit: true,
      delete: true,
      show: true,
    },
    zones: {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    'locker-types': {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    tags: {
      list: true,
      create: true,
      edit: true,
      delete: true,
      show: true,
    },
    'tag-types': {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },
    alerts: {
      list: true,
      create: false,
      edit: true,
      delete: false,
      show: true,
    },
    dashboard: {
      list: true,
      show: true,
    },
  },
  HotelAdmin: {
    // HotelAdmin puede gestionar su hotel
    hotels: {
      list: false,
      create: false,
      edit: true,
      delete: false,
      show: true,
    },
    users: {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    lockers: {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    'locker-controllers': {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    'locker-assignments': {
      list: true,
      create: true,
      edit: true,
      delete: true,
      show: true,
    },
    zones: {
      list: true,
      create: true,
      edit: true,
      delete: true,
      show: true,
    },
    'locker-types': {
      list: true,
      create: true,
      edit: true,
      delete: true,
      show: true,
    },
    tags: {
      list: true,
      create: true,
      edit: true,
      delete: true,
      show: true,
    },
    'tag-types': {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },
    alerts: {
      list: true,
      create: false,
      edit: true,
      delete: false,
      show: true,
    },
    dashboard: {
      list: true,
      show: true,
    },
  },
  Receptionist: {
    // Receptionist solo puede gestionar asignaciones
    lockers: {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },
    'locker-controllers': {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },
    'locker-assignments': {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    zones: {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },
    tags: {
      list: true,
      create: true,
      edit: true,
      delete: false,
      show: true,
    },
    alerts: {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },
    dashboard: {
      list: true,
      show: true,
    },
  },
  User: {
    // Usuario básico solo puede ver
    'locker-assignments': {
      list: true,
      create: false,
      edit: false,
      delete: false,
      show: true,
    },

    dashboard: {
      list: true,
      show: true,
    },
  },
};

/**
 * Verifica si un rol tiene permiso para realizar una acción en un recurso
 */
export const checkResourcePermission = (
  role: UserRole | string,
  resource: string,
  action: ResourceAction
): boolean => {
  // Si no hay rol, no hay permisos
  if (!role) return false;

  const rolePermissions = ROLE_PERMISSIONS[role as UserRole];
  if (!rolePermissions) return false;

  // Verificar permiso comodín (*) para SuperAdmin
  if (rolePermissions['*']) {
    return rolePermissions['*'][action] === true;
  }

  // Verificar permiso específico del recurso
  const resourcePermissions = rolePermissions[resource];
  if (!resourcePermissions) return false;

  return resourcePermissions[action] === true;
};

/**
 * Obtiene todos los permisos de un rol
 */
export const getRolePermissions = (role: UserRole | string): RolePermissions => {
  if (!role) return {};
  return ROLE_PERMISSIONS[role as UserRole] || {};
};

/**
 * Verifica si un rol puede acceder a un recurso (cualquier acción)
 */
export const canAccessResource = (role: UserRole | string, resource: string): boolean => {
  const permissions = getRolePermissions(role);
  
  // Verificar permiso comodín
  if (permissions['*']) {
    return true;
  }
  
  // Verificar si tiene algún permiso en el recurso
  const resourcePermissions = permissions[resource];
  if (!resourcePermissions) return false;
  
  return Object.values(resourcePermissions).some(permission => permission === true);
};

/**
 * Obtiene la lista de recursos disponibles para un rol
 */
export const getAvailableResources = (role: UserRole | string): string[] => {
  const permissions = getRolePermissions(role);
  
  // Si tiene permiso comodín, devolver todos los recursos
  if (permissions['*']) {
    return [
      'dashboard',
      'lockers',
      'locker-assignments',
      'users',
      'hotels',
      'groups',
      'zones',
      'locker-types',
      'tags',
      'tag-types',
      'alerts',
      'system',
    ];
  }
  
  // Devolver solo los recursos con algún permiso
  return Object.keys(permissions).filter(resource => {
    const resourcePerms = permissions[resource];
    return resourcePerms && Object.values(resourcePerms).some(p => p === true);
  });
};

/**
 * Helpers específicos para acciones comunes
 */
export const canCreate = (role: UserRole | string, resource: string): boolean => 
  checkResourcePermission(role, resource, 'create');

export const canEdit = (role: UserRole | string, resource: string): boolean => 
  checkResourcePermission(role, resource, 'edit');

export const canDelete = (role: UserRole | string, resource: string): boolean => 
  checkResourcePermission(role, resource, 'delete');

export const canView = (role: UserRole | string, resource: string): boolean => 
  checkResourcePermission(role, resource, 'show');

export const canList = (role: UserRole | string, resource: string): boolean => 
  checkResourcePermission(role, resource, 'list');

/**
 * Verifica si es un rol de administrador
 */
export const isAdmin = (role: UserRole | string): boolean => {
  return role === 'SuperAdmin' || role === 'GroupAdmin' || role === 'HotelAdmin';
};

/**
 * Verifica si es SuperAdmin
 */
export const isSuperAdmin = (role: UserRole | string): boolean => {
  return role === 'SuperAdmin';
};